import React, { useEffect } from "react";
import * as lucide from "lucide-react";
import { X } from "lucide-react";
import { useLang } from "../i18n";

export function Icon({ name, ...props }) {
  const C = lucide[name] || lucide.Star;
  return <C {...props} />;
}

export function Modal({ open, onClose, title, children, testId, wide = false }) {
  const { t } = useLang();
  useEffect(() => {
    if (!open) return;
    const fn = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid={testId}>
      <div className="absolute inset-0 bg-[#05051f]/85 backdrop-blur-sm" onClick={onClose} />
      <div className={`magic-panel relative w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[85vh] overflow-y-auto p-6 animate-pop`}>
        <button data-testid={`${testId}-close`} onClick={onClose} aria-label={t("common.close")}
          className="absolute top-4 end-4 w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors">
          <X className="w-4 h-4" />
        </button>
        {title && <h3 className="text-xl font-extrabold mb-5 pe-8">{title}</h3>}
        {children}
      </div>
    </div>
  );
}

export function EmptyState({ icon = "Sparkles", title, hint, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center py-10 text-center gap-3">
      <div className="w-16 h-16 rounded-full bg-[#7625D8]/20 border border-[#9C42F5]/30 flex items-center justify-center">
        <Icon name={icon} className="w-8 h-8 text-[#9C42F5]" />
      </div>
      <p className="font-bold text-white whitespace-pre-line">{title}</p>
      {hint && <p className="text-sm text-[#A5A0D6] whitespace-pre-line">{hint}</p>}
    </div>
  );
}

export function StatCard({ icon, label, value, gradient = "from-[#7625D8] to-[#9C42F5]", testId }) {
  return (
    <div data-testid={testId} className="magic-panel magic-panel-hover p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0 shadow-lg`}>
        <Icon name={icon} className="w-6 h-6 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-[#A5A0D6] font-semibold">{label}</p>
        <p className="text-2xl font-black text-white truncate">{value}</p>
      </div>
    </div>
  );
}

export function SkeletonPanel({ lines = 4 }) {
  return (
    <div className="magic-panel p-6 space-y-4 animate-pulse" data-testid="skeleton">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-5 rounded-lg bg-white/8" style={{ width: `${85 - i * 12}%` }} />
      ))}
    </div>
  );
}
